// Component for report actions and controls
import React, { useEffect, useState } from 'react';
import { navigate } from '@reach/router';
import {
  Box,
  TextField,
  Button as MUIButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Switch
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import EventIcon from '@mui/icons-material/EventOutlined';
import ListIcon from '@mui/icons-material/List';
import { Button, TextView } from '@atoms';
import { toast } from 'react-toastify';
import { BillingAccountSelector } from '@organisms';
import { useUploadArtifact, materialTheme } from '@particles';
import { DateTime } from 'luxon';
import { DateRangePicker, Range } from 'react-date-range';
import { processHtmlContent } from './processHtmlContent';

interface ReportControlsProps {
  htmlContent: string | null;
  title: string;
  description: string;
  includeHeaderFooter: boolean;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onIncludeHeaderFooterChange: (value: boolean) => void;
  compact?: boolean;
}

const ReportControls = ({
  htmlContent,
  title,
  description,
  includeHeaderFooter,
  onTitleChange,
  onDescriptionChange,
  onIncludeHeaderFooterChange,
  compact = false
}: ReportControlsProps) => {
  const [artifactLink, setArtifactLink] = useState('');
  const [locationId, setLocationId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [type, setType] = useState<'report' | 'other'>('report');
  const [passwordProtected, setPasswordProtected] = useState(false);
  const [password, setPassword] = useState('');

  // Use React Query mutation
  const uploadArtifactMutation = useUploadArtifact();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const [range, setRange] = useState<Range[]>([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);

  useEffect(() => {
    const date = new Date();
    // These variables can be used in future implementations if needed
    // const start = range[0]?.startDate ? range[0].startDate : date;
    const end = range[0]?.endDate ? range[0].endDate : date;

    const newEnd = new Date(end);
    newEnd.setHours(23);
    newEnd.setMinutes(59);
    newEnd.setSeconds(59);
  }, [range]);

  const handleCopyLink = () => {
    if (artifactLink) {
      navigator.clipboard.writeText(artifactLink);
      toast.success('Artifact link copied to clipboard');
    }
  };

  const openGenerateModal = () => {
    if (!htmlContent) {
      toast.error('Please add HTML content to generate the report');
      return;
    }
    setModalOpen(true);
  };

  const closeGenerateModal = () => {
    setModalOpen(false);
  };

  const generateArtifactLink = () => {
    if (!htmlContent) {
      toast.error('Please add HTML content to generate the report');
      return;
    }

    // Generate share ID for the artifact URL (must match server expectation)
    const shareId = crypto.randomUUID().replace(/-/g, '').substring(0, 8);
    const shareUrl = `${process.env.REACT_APP_API_BASE}/a/${shareId}`;

    const reportTitle = title || 'SalesRook Report';
    const reportDescription =
      description ||
      'SalesRook - Advanced sales analytics and reporting platform for data-driven business growth';

    // Process HTML with header and meta tags on the frontend
    const processedHtml = processHtmlContent(
      htmlContent,
      shareUrl,
      reportTitle,
      reportDescription,
      includeHeaderFooter
    );

    if (passwordProtected && !password.trim()) {
      toast.error('Please enter a password or disable password protection');
      return;
    }

    // Create form data with processed HTML
    const formData = new FormData();
    const htmlBlob = new Blob([processedHtml], { type: 'text/html' });
    const fileName = `report-${Date.now()}.html`;
    formData.append('file', htmlBlob, fileName);
    formData.append('type', type);
    formData.append('title', reportTitle);
    formData.append('description', reportDescription);
    formData.append('shareId', shareId);
    if (passwordProtected && password.trim()) {
      formData.append('password', password);
    }

    if (type === 'report') {
      if (!locationId) {
        toast.error('Please select a location to generate the report');
        return;
      }

      if (!range[0].startDate || !range[0].endDate) {
        toast.error('Please select a valid date range');
        return;
      }
      formData.append('locationId', locationId);

      // Get selected date range as date-only strings (no timezone shift)
      const startDate = range[0].startDate
        ? DateTime.fromJSDate(range[0].startDate).toISODate()
        : DateTime.now().toISODate();
      const endDate = range[0].endDate
        ? DateTime.fromJSDate(range[0].endDate).toISODate()
        : DateTime.now().toISODate();

      formData.append('startDate', startDate || '');
      formData.append('endDate', endDate || '');
    }

    // Use React Query mutation
    uploadArtifactMutation.mutate(
      { formData },
      {
        onSuccess: (response: { data?: { shareUrl?: string } }) => {
          closeGenerateModal();
          
          // Server returns shareUrl as a short /a/{fileName} path. Combine
          // with the API base so the clipboard copy resolves for anyone
          // pasting the link elsewhere.
          const sharePath = response?.data?.shareUrl ?? '';
          const shareUrl = sharePath ? `${process.env.REACT_APP_API_BASE}${sharePath}` : '';
          setArtifactLink(shareUrl);
          toast.success('Artifact link generated successfully!');

          // Clear form data after successful generation
          onTitleChange('');
          onDescriptionChange('');
          setLocationId('');
          setType('report');
          setPasswordProtected(false);
          setPassword('');
          // Reset date range to current date
          setRange([
            {
              startDate: new Date(),
              endDate: new Date(),
              key: 'selection'
            }
          ]);
        },
        onError: (error: unknown) => {
          closeGenerateModal();
          const errorMessage = error && typeof error === 'object' && 'error' in error 
            ? String(error.error) 
            : 'Unknown error';
          toast.error(`Error uploading report: ${errorMessage}`);
        }
      }
    );
  };

  if (compact) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeHeaderFooter}
                onChange={(e) => onIncludeHeaderFooterChange(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <TextView variant="body2" $fontSize={13}>
                Header & footer
              </TextView>
            }
          />
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            size="small"
            onClick={openGenerateModal}
            disabled={!htmlContent}
            sx={{ height: 36 }}>
            Generate Link
          </Button>
          {artifactLink && (
            <MUIButton
              onClick={handleCopyLink}
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon fontSize="small" />}>
              Copy Link
            </MUIButton>
          )}
        </Box>
        {artifactLink && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: 2,
              maxWidth: 420,
              zIndex: 1300
            }}>
            <TextView variant="subtitle2" $fontWeight={600} sx={{ mb: 1 }}>
              Artifact Link
            </TextView>
            <TextField
              value={artifactLink}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: '0.75rem' },
                endAdornment: (
                  <MUIButton onClick={handleCopyLink} size="small" sx={{ ml: 0.5 }}>
                    <ContentCopyIcon fontSize="small" />
                  </MUIButton>
                )
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<ListIcon />}
              onClick={() => navigate('/artifacts')}
              sx={{ mt: 1 }}>
              View All
            </Button>
          </Box>
        )}
        <Dialog open={modalOpen} onClose={closeGenerateModal} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LinkIcon sx={{ mr: 1, color: materialTheme.palette.primary.main }} />
              <TextView variant="h6" $fontWeight={600}>
                Generate Artifact Link
              </TextView>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <TextView
                variant="subtitle1"
                $fontWeight={600}
                sx={{ mb: 2, color: materialTheme.palette.primary.main }}>
                Basic Information
              </TextView>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Artifact Title"
                    value={title}
                    size="small"
                    onChange={(e) => onTitleChange(e.target.value)}
                    fullWidth
                    sx={{ mb: 1 }}
                    placeholder="Enter a descriptive title"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="report-type-label">Artifact Type</InputLabel>
                    <Select
                      labelId="report-type-label"
                      id="report-type-select"
                      value={type}
                      size="small"
                      label="Artifact Type"
                      onChange={(e) => setType(e.target.value as 'report' | 'other')}
                      sx={{ minHeight: '40px' }}>
                      <MenuItem value="report">Report</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Artifact Description"
                    value={description}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Provide a detailed description of this artifact"
                  />
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={passwordProtected}
                    onChange={(e) => {
                      setPasswordProtected(e.target.checked);
                      if (!e.target.checked) setPassword('');
                    }}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <TextView variant="body2">
                    Password protect this artifact
                  </TextView>
                }
              />
              {passwordProtected && (
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  size="small"
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  autoComplete="new-password"
                  placeholder="Viewers will need this password to open the link"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
            {type === 'report' && (
              <Box sx={{ mb: 1, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                <TextView
                  variant="subtitle1"
                  $fontWeight={600}
                  sx={{ mb: 2, color: materialTheme.palette.primary.main }}>
                  Report Details <span style={{ color: 'red' }}>*</span>
                </TextView>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <TextView $fontWeight={600} $fontSize={14} sx={{ mb: 1 }}>
                        Select Location <span style={{ color: 'red' }}>*</span>
                      </TextView>
                      <BillingAccountSelector
                        type="c"
                        status="live"
                        caller="report-controls-location-1"
                        title="Search Location"
                        width="100%"
                        onSelected={(account) => setLocationId(account?.locationId ?? '')}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <TextView $fontWeight={600} $fontSize={14} sx={{ mb: 1 }}>
                        Select Date Range <span style={{ color: 'red' }}>*</span>
                      </TextView>
                      <Button
                        aria-describedby={id}
                        sx={{
                          width: '63%',
                          color: materialTheme.palette.text.primary,
                          justifyContent: 'flex-start',
                          p: 1.5,
                          bgcolor: '#fff'
                        }}
                        size="small"
                        startIcon={<EventIcon color="primary" />}
                        variant="outlined"
                        onClick={handleClick}>
                        <TextView $fontWeight={400} $fontSize={14} sx={{ flexGrow: 1 }}>
                          {DateTime.fromJSDate(
                            range[0].startDate ? range[0]?.startDate : new Date()
                          ).toFormat('LLLL dd yyyy')}
                          {` - `}
                          {DateTime.fromJSDate(
                            range[0].endDate ? range[0]?.endDate : new Date()
                          ).toFormat('LLLL dd yyyy')}
                        </TextView>
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                <TextView variant="caption" color="error" $fontSize={12}>
                  * All fields are required for report type artifacts
                </TextView>
              </Box>
            )}
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              PaperProps={{ sx: { boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}>
              <DateRangePicker
                onChange={(item) => setRange([item.selection])}
                moveRangeOnFirstSelection={false}
                months={2}
                ranges={range}
                direction="horizontal"
              />
            </Popover>
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Box>
              {type === 'report' && (
                <TextView variant="caption" color="textSecondary" $fontSize={12}>
                  This will generate a shareable report link based on the selected criteria
                </TextView>
              )}
            </Box>
            <Box>
              <Button variant="outlined" onClick={closeGenerateModal} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={generateArtifactLink}
                loading={uploadArtifactMutation.isPending}
                disabled={
                  type === 'report' ? !locationId || !range[0].startDate || !range[0].endDate : false
                }
                startIcon={<LinkIcon />}>
                Generate Link
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8f9fa', mb: 3 }}>
      <Grid container>
        <Grid item xs={12} md={8}>
          <TextView display={'initial'} $fontSize={26}>
            Artifacts
          </TextView>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeHeaderFooter}
                  onChange={(e) => onIncludeHeaderFooterChange(e.target.checked)}
                  color="primary"
                />
              }
              label="Include SalesRook header & footer"
              sx={{ mb: 2, display: 'block' }}
            />
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              fullWidth
              onClick={openGenerateModal}
              disabled={!htmlContent}
              sx={{ height: '45px', mb: 2 }}>
              Generate Artifact Link
            </Button>

            {artifactLink && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: '#fff',
                  borderRadius: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                <TextView variant="subtitle2" $fontWeight={600} sx={{ mb: 1 }}>
                  Generated Artifact Link
                </TextView>
                <TextField
                  value={artifactLink}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '0.875rem', height: '45px' },
                    endAdornment: (
                      <MUIButton
                        onClick={handleCopyLink}
                        sx={{ minWidth: 'auto', ml: 1 }}
                        variant="outlined"
                        size="small">
                        <ContentCopyIcon fontSize="small" sx={{ mr: 0.5 }} /> Copy
                      </MUIButton>
                    )
                  }}
                  sx={{ mb: 2 }}
                />
                <TextView variant="caption" color="textSecondary" $fontSize={12} sx={{ display: 'block', mb: 2 }}>
                  This link can be shared to access the HTML report
                </TextView>
                <Button
                  variant="outlined"
                  startIcon={<ListIcon />}
                  fullWidth
                  onClick={() => navigate('/artifacts')}
                  sx={{ height: '40px' }}>
                  View All Artifacts
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Modal for Location Selection and Link Generation */}
      <Dialog open={modalOpen} onClose={closeGenerateModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LinkIcon sx={{ mr: 1, color: materialTheme.palette.primary.main }} />
            <TextView variant="h6" $fontWeight={600}>
              Generate Artifact Link
            </TextView>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Basic Information Section */}
          <Box sx={{ mb: 3 }}>
            <TextView
              variant="subtitle1"
              $fontWeight={600}
              sx={{ mb: 2, color: materialTheme.palette.primary.main }}>
              Basic Information
            </TextView>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Artifact Title"
                  value={title}
                  size="small"
                  onChange={(e) => onTitleChange(e.target.value)}
                  fullWidth
                  sx={{ mb: 1 }}
                  placeholder="Enter a descriptive title"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="report-type-label">Artifact Type</InputLabel>
                  <Select
                    labelId="report-type-label"
                    id="report-type-select"
                    value={type}
                    size="small"
                    label="Artifact Type"
                    onChange={(e) => setType(e.target.value as 'report' | 'other')}
                    sx={{ minHeight: '40px' }}>
                    <MenuItem value="report">Report</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Artifact Description"
                  value={description}
                  onChange={(e) => onDescriptionChange(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Provide a detailed description of this artifact"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Report-specific Section */}
          {type === 'report' && (
            <Box sx={{ mb: 1, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <TextView
                variant="subtitle1"
                $fontWeight={600}
                sx={{ mb: 2, color: materialTheme.palette.primary.main }}>
                Report Details <span style={{ color: 'red' }}>*</span>
              </TextView>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <TextView $fontWeight={600} $fontSize={14} sx={{ mb: 1 }}>
                      Select Location <span style={{ color: 'red' }}>*</span>
                    </TextView>
                    <BillingAccountSelector
                      type="c"
                      status="live"
                      caller="report-controls-location-2"
                      title="Search Location"
                      width="100%"
                      onSelected={(account) => setLocationId(account?.locationId ?? '')}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <TextView $fontWeight={600} $fontSize={14} sx={{ mb: 1 }}>
                      Select Date Range <span style={{ color: 'red' }}>*</span>
                    </TextView>
                    <Button
                      aria-describedby={id}
                      sx={{
                        width: '63%',
                        color: materialTheme.palette.text.primary,
                        justifyContent: 'flex-start',
                        p: 1.5,
                        bgcolor: '#fff'
                      }}
                      size="small"
                      
                      startIcon={<EventIcon color="primary" />}
                      variant={'outlined'}
                      onClick={handleClick}>
                      <TextView $fontWeight={400} $fontSize={14} sx={{ flexGrow: 1 }}>
                        {DateTime.fromJSDate(
                          range[0].startDate ? range[0]?.startDate : new Date()
                        ).toFormat('LLLL dd yyyy')}
                        {` - `}
                        {DateTime.fromJSDate(
                          range[0].endDate ? range[0]?.endDate : new Date()
                        ).toFormat('LLLL dd yyyy')}
                      </TextView>
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <TextView variant="caption" color="error" $fontSize={12}>
                  * All fields are required for report type artifacts
                </TextView>
              </Box>
            </Box>
          )}

          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            PaperProps={{
              sx: { boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }
            }}>
            <DateRangePicker
              onChange={(item) => setRange([item.selection])}
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={range}
              direction="horizontal"
            />
          </Popover>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Box>
            {type === 'report' && (
              <TextView variant="caption" color="textSecondary" $fontSize={12}>
                This will generate a shareable report link based on the selected criteria
              </TextView>
            )}
          </Box>
          <Box>
            <Button variant="outlined" onClick={closeGenerateModal} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={generateArtifactLink}
              loading={uploadArtifactMutation.isPending}
              disabled={
                type === 'report' ? !locationId || !range[0].startDate || !range[0].endDate : false
              }
              startIcon={<LinkIcon />}>
              Generate Link
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportControls;
